/**
 * Terminated employees who still have worklists assigned — an error page.
 *
 * Shayna, 2026-09-25 review: "let the data stay… we make an error page." Nothing here
 * or in the pipeline reassigns these claims. They stay on the terminated person until
 * someone else is assigned the worklist, so the page lists who to fix at the source.
 *
 * The rows come straight off the bundle, not off the people list: most of these
 * people were not paid in the period and are not in it. The API scopes them by the
 * same grant as every other people row (api/shared/scope.ts).
 *
 * 2026-10-01 (Shayna): a count opens the open claims behind it, and the export carries
 * the Paylocity ID instead of the FCCKey. The claims travel nested in each person's
 * row, so they are scoped with it. They are the open pile at the snapshot, not Claim
 * detail's billed claims, which is why a count does not link to Claim detail. A claim's
 * Visit ID does (2026-10-21): it opens that visit there, when it was billed.
 *
 * 2026-10-14 (Shayna, #398): a second section, the claims on "No assignment" lists by the
 * biller who last held them (`NoAssignmentSection`).
 */
import { useMemo } from 'react'
import { useUrlState, bool, json } from '@/ui/urlState'
import { useDataset } from '@/data/DatasetProvider'
import { useFilters } from '@/data/filters'
import type { TerminatedWorklistRow } from '@/data/types'
import { DataTable, type Column } from '@/components/Table'
import { StatStrip, Stat, PersonCell } from '@/components/Bits'
import { ExportBar } from '@/components/ExportBar'
import { openClaimColumns, OPEN_CLAIM_EXPORT } from '@/components/OpenClaimTable'
import { PopOut } from '@/components/PopOut'
import { SidePanel } from '@/components/SidePanel'
import {
  UNASSIGNED,
  claimsIn,
  decodeClaims,
  paylocityOf,
  sameScope,
  scopeLabel,
  type ClaimScope,
} from '@/data/terminatedClaims'
import { dateLong, int, monthYear } from '@/format'
import { NoAssignmentSection } from './NoAssignmentSection'
import { useOpenClaimVisit } from '@/data/claimDrill'
import { usePageAllowed } from '@/access/pageLimit'

const matches = (sel: string[], value: string) => sel.length === 0 || sel.includes(value)

/** The CSV is one row per person × worklist: the list someone works through to fix. */
interface FixRow {
  row: TerminatedWorklistRow
  worklist: TerminatedWorklistRow['worklists'][number]
}

/** A count that opens its claims, or plain text when it cannot. */
function Count({ n, onOpen, label }: { n: number; onOpen: (() => void) | null; label: string }) {
  if (n <= 0) return <span className="mono tw-count">—</span>
  if (!onOpen) return <span className="mono tw-count">{int(n)}</span>
  return (
    <button
      type="button"
      className="mono tw-count tw-open"
      onClick={onOpen}
      title={`Show these ${int(n)} open claims (${label})`}
    >
      {int(n)}
    </button>
  )
}

export function TerminatedWorklists() {
  const { dataset } = useDataset()
  const { filters } = useFilters()
  // Kept for Back and for a copied link (ui/urlState.ts): the toggle in the URL, the
  // person whose claims are open in the history entry.
  const [openOnly, setOpenOnly] = useUrlState('withopen', false, bool)
  const [opened, setOpened] = useUrlState(
    'claims',
    null as { employee: string; scope: ClaimScope } | null,
    json<{ employee: string; scope: ClaimScope } | null>(null),
    { personal: true },
  )

  const all = dataset?.terminatedWorklists
  const open = useMemo(() => {
    const row = opened && all?.find((r) => r.employee === opened.employee)
    return row ? { row, scope: opened.scope } : null
  }, [opened, all])
  const setOpen = (o: { row: TerminatedWorklistRow; scope: ClaimScope } | null) =>
    setOpened(o ? { employee: o.row.employee, scope: o.scope } : null)
  const snapshot = dataset?.worklistSnapshot ?? null
  const employees = dataset?.employees
  // Bundles built before 2026-10-02 carry counts only. Then nothing opens, and the
  // rest of a person's total is one line, since it cannot be split.
  const hasClaims = (all ?? []).every((r) => r.claims !== undefined)
  const hasPaylocity = (all ?? []).every((r) => r.paylocityId !== undefined)

  // Department and Job title are the only header filters that mean anything here.
  // The rest describe paid people in the period, which these mostly are not.
  const rows = useMemo(
    () =>
      (all ?? []).filter(
        (r) =>
          matches(filters.department, r.department) &&
          matches(filters.jobTitle, r.jobTitle) &&
          (!openOnly || r.openClaims > 0),
      ),
    [all, filters.department, filters.jobTitle, openOnly],
  )

  const s = useMemo(() => {
    const worklists = rows.flatMap((r) => r.worklists)
    return {
      people: rows.length,
      worklists: worklists.length,
      disabled: worklists.filter((w) => !w.enabled).length,
      open: rows.reduce((a, r) => a + r.openClaims, 0),
      withOpen: rows.filter((r) => r.openClaims > 0).length,
    }
  }, [rows])

  const fixRows = useMemo<FixRow[]>(
    () => rows.flatMap((row) => row.worklists.map((worklist) => ({ row, worklist }))),
    [rows],
  )

  const columns: Column<TerminatedWorklistRow>[] = [
    {
      key: 'person',
      header: 'Employee',
      width: 210,
      sort: (r) => r.employee,
      render: (r) => <PersonCell name={r.employee} meta={r.jobTitle} />,
    },
    {
      key: 'terminated',
      header: 'Terminated',
      width: 110,
      sort: (r) => r.terminationDate,
      render: (r) => (
        <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
          {dateLong(r.terminationDate)}
        </span>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      width: 140,
      sort: (r) => r.department,
      render: (r) => r.department,
    },
    {
      key: 'worklists',
      header: 'Worklists still assigned',
      sort: (r) => r.worklists.length,
      render: (r) => {
        const opener = (scope: ClaimScope) => (r.claims ? () => setOpen({ row: r, scope }) : null)
        const onLists = r.worklists.reduce((a, w) => a + w.openClaims, 0)
        return (
          <ul className="tw-list">
            {r.worklists.map((w) => (
              <li key={w.name}>
                <span className="tw-name">{w.name}</span>
                {!w.enabled ? (
                  <span className="pts-chip neut" title="Disabled in the worklist configuration">
                    disabled
                  </span>
                ) : (
                  <span />
                )}
                <Count
                  n={w.openClaims}
                  label={w.name}
                  onOpen={opener({ kind: 'worklist', name: w.name })}
                />
              </li>
            ))}
            {r.unassignedClaims !== undefined ? (
              <>
                {r.unassignedClaims > 0 && (
                  <li className="tw-extra">
                    <span className="tw-name">{UNASSIGNED}</span>
                    <span />
                    <Count
                      n={r.unassignedClaims}
                      label={UNASSIGNED}
                      onOpen={opener({ kind: 'unassigned' })}
                    />
                  </li>
                )}
                {(r.otherWorklistClaims ?? 0) > 0 && (
                  <li
                    className="tw-extra"
                    title="Still credited to them, on a worklist no longer assigned to them"
                  >
                    <span className="tw-name">Other worklists</span>
                    <span />
                    <Count
                      n={r.otherWorklistClaims ?? 0}
                      label="other worklists"
                      onOpen={opener({ kind: 'other' })}
                    />
                  </li>
                )}
              </>
            ) : (
              r.openClaims > onLists && (
                <li className="tw-extra">
                  <span className="tw-name">Unassigned and other worklists</span>
                  <span />
                  <Count n={r.openClaims - onLists} label="" onOpen={null} />
                </li>
              )
            )}
          </ul>
        )
      },
    },
    {
      key: 'open',
      header: 'Open claims',
      align: 'right',
      width: 110,
      title: 'The person’s whole open pile at the snapshot, including claims on Unassigned',
      sort: (r) => r.openClaims,
      render: (r) => (
        <span style={{ fontSize: 'var(--t-table)' }}>
          <Count
            n={r.openClaims}
            label="all of them"
            onOpen={r.claims ? () => setOpen({ row: r, scope: { kind: 'all' } }) : null}
          />
        </span>
      ),
    },
  ]

  return (
    <>
      <h1 className="page-title">Terminated employees with worklists still assigned</h1>
      <p className="body-note" style={{ maxWidth: 760, marginTop: 6 }}>
        Each of these people has left, and the worklist configuration still assigns work to
        them. Nothing reassigns it automatically: their open claims stay with them until
        someone else is assigned the worklist. Reassign it in the worklist configuration and
        they drop off this list on the next refresh.
      </p>

      {!all ? (
        <div className="notice" role="note" style={{ marginTop: 16 }}>
          <span className="eyebrow">Not in this bundle</span>
          <p className="notice-what">This bundle has no worklist assignments.</p>
          <p className="notice-why">
            It was built before the pipeline carried them, or without the open-claims
            snapshot. The next refresh fills this page in.
          </p>
        </div>
      ) : (
        <>
          <div style={{ marginTop: 16 }}>
            <StatStrip cols={4}>
              <Stat
                label="People"
                value={int(s.people)}
                note={`${int(s.withOpen)} with open claims`}
                hero
              />
              <Stat
                label="Worklists still assigned"
                value={int(s.worklists)}
                note={s.disabled ? `${int(s.disabled)} disabled` : undefined}
              />
              <Stat
                label="Open claims"
                value={int(s.open)}
                note={snapshot ? `latest snapshot, ${monthYear(snapshot)}` : undefined}
              />
            </StatStrip>
          </div>

          <PopOut title="Who still holds work" style={{ marginTop: 16 }}>
            {({ button, enlarged }) => (
              <div className="card popout-fill">
                <div className="tw-head">
                  {!enlarged && <h3 className="card-heading">Who still holds work</h3>}
                  <label className="tw-toggle">
                    <input
                      type="checkbox"
                      checked={openOnly}
                      onChange={(e) => setOpenOnly(e.target.checked)}
                    />
                    Only people with open claims
                  </label>
                  {button}
                </div>
                <ExportBar
                  filename="terminated-worklists.csv"
                  rows={fixRows}
                  columns={[
                    { header: 'Employee', value: (f) => f.row.employee },
                    { header: 'Paylocity ID', value: (f) => paylocityOf(f.row, employees ?? []) },
                    { header: 'Termination date', value: (f) => f.row.terminationDate },
                    { header: 'Job title', value: (f) => f.row.jobTitle },
                    { header: 'Department', value: (f) => f.row.department },
                    { header: 'Worklist', value: (f) => f.worklist.name },
                    { header: 'Enabled', value: (f) => (f.worklist.enabled ? 'Yes' : 'No') },
                    { header: 'Open claims on worklist', value: (f) => f.worklist.openClaims },
                    { header: 'Open claims on Unassigned', value: (f) => f.row.unassignedClaims ?? '' },
                    {
                      header: 'Open claims on other worklists',
                      value: (f) => f.row.otherWorklistClaims ?? '',
                    },
                    { header: 'Open claims (person)', value: (f) => f.row.openClaims },
                  ]}
                />
                <div className="card emp-dense" style={{ padding: 0, overflow: 'hidden' }}>
                  <DataTable
                    storageKey="terminated-worklists.people"
                    rows={rows}
                    columns={columns}
                    rowKey={(r) => r.fccKeys.join(',')}
                    initialSort="open"
                    initialDir="desc"
                    sortParam="sort"
                    maxHeight={640}
                    empty="No terminated employee in this scope still has a worklist assigned."
                  />
                </div>
                <p className="body-note" style={{ marginTop: 10 }}>
                  Per worklist, the figure is the open claims on that worklist. The person’s
                  total also counts their claims on <span className="mono">{UNASSIGNED}</span> and
                  on worklists no longer assigned to them, each on a line of its own, so the lines
                  add up to the total.
                  {hasClaims && ' Click a count to see the claims behind it.'} The data holds who
                  has a worklist now, not since when it was assigned.
                </p>
                {(!hasClaims || !hasPaylocity) && (
                  <p className="body-note tw-older" role="note" style={{ marginTop: 6 }}>
                    {hasPaylocity
                      ? 'This build carries no claim lists — they did not add up to the counts, so the refresh left them out — and the counts do not open. The next refresh may bring them back.'
                      : 'This data was built before the claim list and the Paylocity ID were added, so the counts do not open their claims, and the export’s Paylocity ID comes from the people list — blank for anyone not on it. The next refresh fills them in.'}
                  </p>
                )}
              </div>
            )}
          </PopOut>
        </>
      )}

      {dataset?.noAssignment ? (
        <NoAssignmentSection
          section={dataset.noAssignment}
          department={filters.department}
          jobTitle={filters.jobTitle}
          snapshot={snapshot}
          asOf={dataset.worklistAsOf ?? null}
        />
      ) : (
        dataset?.noAssignment === null && (
          <p className="body-note tw-older" role="note" style={{ marginTop: 24 }}>
            Claims on No assignment lists: this refresh could not build them. The next one may.
          </p>
        )
      )}

      {open && open.row.claims && (
        <OpenClaimsPanel
          row={open.row}
          scope={open.scope}
          snapshot={snapshot}
          paylocityId={paylocityOf(open.row, employees ?? [])}
          onClose={() => setOpen(null)}
          onScope={(scope) => setOpen({ row: open.row, scope })}
        />
      )}
    </>
  )
}

const MAX_ROWS = 500

/** One person's open claims on one of their lines, or all of them. */
function OpenClaimsPanel({
  row,
  scope,
  snapshot,
  paylocityId,
  onClose,
  onScope,
}: {
  row: TerminatedWorklistRow
  scope: ClaimScope
  snapshot: string | null
  paylocityId: string
  onClose: () => void
  onScope: (scope: ClaimScope) => void
}) {
  const claims = useMemo(() => (row.claims ? decodeClaims(row.claims) : []), [row])
  const shown = useMemo(() => claimsIn(row, claims, scope), [row, claims, scope])
  // One worklist's claims all sit on it; the column only says something otherwise.
  const oneList = scope.kind === 'worklist' || scope.kind === 'unassigned'
  const openVisit = useOpenClaimVisit()
  const toClaims = usePageAllowed('/claim-detail')

  const columns = openClaimColumns(!oneList, toClaims ? openVisit : undefined)

  const lines: ClaimScope[] = [
    { kind: 'all' },
    ...row.worklists
      .filter((w) => w.openClaims > 0)
      .map((w): ClaimScope => ({ kind: 'worklist', name: w.name })),
    ...((row.unassignedClaims ?? 0) > 0 ? [{ kind: 'unassigned' } as const] : []),
    ...((row.otherWorklistClaims ?? 0) > 0 ? [{ kind: 'other' } as const] : []),
  ]

  return (
    <SidePanel onClose={onClose} label={`Open claims of ${row.employee}: ${scopeLabel(scope)}`}>
      <span className="eyebrow">
        Open claims{snapshot ? `, ${monthYear(snapshot)} snapshot` : ''}
      </span>
      <h2 className="page-title" style={{ marginTop: 4 }}>
        {row.employee}
      </h2>
      <p className="body-note" style={{ marginTop: 4 }}>
        {row.jobTitle} · terminated {dateLong(row.terminationDate)}
        {paylocityId ? ` · Paylocity ID ${paylocityId}` : ''}
      </p>

      <div className="tw-scopes" role="group" aria-label="Which claims">
        {lines.map((l) => (
          <button
            key={`${l.kind}:${scopeLabel(l)}`}
            type="button"
            className={sameScope(l, scope) ? 'on' : undefined}
            aria-pressed={sameScope(l, scope)}
            onClick={() => onScope(l)}
          >
            {scopeLabel(l)} · {int(claimsIn(row, claims, l).length)}
          </button>
        ))}
      </div>

      <ExportBar
        filename="terminated-open-claims.csv"
        rows={shown}
        columns={[
          { header: 'Employee', value: () => row.employee },
          { header: 'Paylocity ID', value: () => paylocityId },
          ...OPEN_CLAIM_EXPORT,
        ]}
      />
      <div className="card emp-dense" style={{ padding: 0, overflow: 'hidden' }}>
        <DataTable
          storageKey="terminated-worklists.claims"
          sortParam="claims.sort"
          rows={shown}
          columns={columns}
          rowKey={(c) => c.claimId}
          initialSort="bill"
          initialDir="asc"
          // A pile runs to thousands; drawing them all took ~5 s. Sorting still covers
          // every claim, and the export carries all of them.
          maxRows={MAX_ROWS}
          maxHeight="calc(100vh - 280px)"
          empty="No open claims here."
        />
      </div>
      <p className="body-note" style={{ marginTop: 10 }}>
        {shown.length > MAX_ROWS &&
          `The table shows the first ${int(MAX_ROWS)} in the current sort; the export has all ${int(shown.length)}. `}
        The open pile at the snapshot, not billed claims: most of these are not on Claim
        detail, which lists billed claims in months the person was paid. A claim with no
        visit behind it yet has no payer, bill date or balance.
      </p>
    </SidePanel>
  )
}
